import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Users, 
  Building2, 
  Calendar, 
  DollarSign,
  Search,
  Eye,
  Shield,
  MapPin,
  Clock,
  Trash2,
  Ban,
  CheckCircle,
  UserX,
  Building,
  Flag,
  AlertTriangle
} from "lucide-react";

const AdminPanel = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Users
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [userRole, setUserRole] = useState("all");
  
  // Matches
  const [matches, setMatches] = useState([]);
  const [matchStatus, setMatchStatus] = useState("all");
  
  // Bookings
  const [bookings, setBookings] = useState([]);
  const [bookingStatus, setBookingStatus] = useState("all");
  
  // Venues
  const [venues, setVenues] = useState([]);

  // Delete Dialog State
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, name: null });

  // Listen to URL changes and update active tab
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && ['users', 'matches', 'bookings', 'venues'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [location.search]);

  useEffect(() => {
    // Check if user is admin
    if (!user?.isAdmin) {
      toast.error("Bu sayfaya erişim yetkiniz yok");
      navigate("/");
      return;
    }
    
    fetchStats();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (activeTab === "users") fetchUsers();
    if (activeTab === "matches") fetchMatches();
    if (activeTab === "bookings") fetchBookings();
    if (activeTab === "venues") fetchVenues();
  }, [activeTab, userRole, userSearch, matchStatus, bookingStatus]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("İstatistikler yüklenemedi");
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (userRole !== "all") params.role = userRole;
      if (userSearch) params.search = userSearch;

      const response = await axios.get(`${API}/admin/users`, { params });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Kullanıcılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const params = {};
      if (matchStatus !== "all") params.status = matchStatus;
      
      const response = await axios.get(`${API}/admin/matches`, { params });
      setMatches(response.data);
    } catch (error) {
      console.error("Error fetching matches:", error);
      toast.error("Maçlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = {};
      if (bookingStatus !== "all") params.status = bookingStatus;
      
      const response = await axios.get(`${API}/admin/bookings`, { params });
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Rezervasyonlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchVenues = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/venues`);
      setVenues(response.data);
    } catch (error) {
      console.error("Error fetching venues:", error);
      toast.error("Sahalar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      player: "Oyuncu",
      venue_owner: "Saha Sahibi",
      captain: "Kaptan"
    };
    return labels[role] || role;
  };

  // User Actions
  const handleDeleteUser = async (userId, userName) => {
    setDeleteDialog({ open: true, type: 'user', id: userId, name: userName });
  };

  const confirmDelete = async () => {
    const { type, id, name } = deleteDialog;
    
    try {
      if (type === 'user') {
        await axios.delete(`${API}/admin/users/${id}`);
        toast.success(`"${name}" kullanıcısı silindi`);
        fetchUsers();
      } else if (type === 'venue') {
        await axios.delete(`${API}/admin/venues/${id}`);
        toast.success(`"${name}" sahası silindi`);
        fetchVenues();
      }
      fetchStats();
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error(error.response?.data?.detail || "Silme işlemi başarısız");
    } finally {
      setDeleteDialog({ open: false, type: null, id: null, name: null });
    }
  };

  const handleBlockUser = async (userId, isBlocked) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/block`);
      toast.success(isBlocked ? "Kullanıcı engeli kaldırıldı" : "Kullanıcı engellendi");
      fetchUsers();
    } catch (error) {
      console.error("Error blocking user:", error);
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  // Venue Actions
  const handleDeleteVenue = async (venueId, venueName) => {
    setDeleteDialog({ open: true, type: 'venue', id: venueId, name: venueName });
  };

  const handleBlockVenue = async (venueId, isBlocked) => {
    try {
      await axios.put(`${API}/admin/venues/${venueId}/block`);
      toast.success(isBlocked ? "Saha engeli kaldırıldı" : "Saha engellendi");
      fetchVenues();
    } catch (error) {
      console.error("Error blocking venue:", error);
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };


  const getRoleBadgeColor = (role) => {
    const colors = {
      player: "bg-blue-500",
      venue_owner: "bg-emerald-500",
      captain: "bg-purple-500"
    };
    return colors[role] || "bg-gray-500";
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: { bg: "bg-green-500", text: "Açık" },
      closed: { bg: "bg-gray-500", text: "Kapalı" },
      confirmed: { bg: "bg-blue-500", text: "Onaylı" },
      cancelled: { bg: "bg-red-500", text: "İptal" },
      pending: { bg: "bg-yellow-500", text: "Beklemede" }
    };
    return styles[status] || { bg: "bg-gray-500", text: status };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("tr-TR", { 
      year: "numeric", 
      month: "short", 
      day: "numeric" 
    });
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-violet-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-violet-50">
      <Navbar user={user} onLogout={onLogout} />

      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent flex items-center gap-3">
            <Shield className="h-8 w-8 text-purple-600" />
            Admin Paneli
          </h1>
          <p className="text-base text-gray-600">Platform yönetimi ve istatistikler</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Kullanıcılar</CardTitle>
                <Users className="h-4 w-4 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600">{stats.users.total}</div>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.users.players} Oyuncu, {stats.users.venueOwners} Saha Sahibi
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Sahalar</CardTitle>
                <Building2 className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">{stats.venues.total}</div>
                <p className="text-xs text-gray-500 mt-1">Kayıtlı saha</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Maçlar</CardTitle>
                <Calendar className="h-4 w-4 text-violet-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-violet-600">{stats.matches.total}</div>
                <p className="text-xs text-gray-500 mt-1">{stats.matches.active} Aktif</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Gelir</CardTitle>
                <DollarSign className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">
                  ₺{stats.revenue.total.toFixed(2)}
                </div>
                <p className="text-xs text-gray-500 mt-1">{stats.bookings.confirmed} Rezervasyon</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Hızlı Erişim Kartları */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-emerald-500"
            onClick={() => navigate("/admin/venues")}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <Building2 className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Saha Yönetimi</h3>
                  <p className="text-sm text-gray-500">Yeni saha ekle ve talepleri yönet</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-orange-500"
            onClick={() => navigate("/admin/tournaments")}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Calendar className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Turnuva Yönetimi</h3>
                  <p className="text-sm text-gray-500">Turnuva oluştur ve düzenle</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-purple-500"
            onClick={() => navigate("/admin-profile")}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Shield className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Admin Profili</h3>
                  <p className="text-sm text-gray-500">Hesap ayarları ve güvenlik</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-red-500"
            onClick={() => navigate("/admin/reports")}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-lg">
                  <Flag className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Kullanıcı Raporları</h3>
                  <p className="text-sm text-gray-500">Şikayetleri incele ve yönet</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(newTab) => {
          setActiveTab(newTab);
          navigate(`/admin?tab=${newTab}`, { replace: true });
        }}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="users">
              <Users className="h-4 w-4 mr-2" />
              Kullanıcılar
            </TabsTrigger>
            <TabsTrigger value="matches">
              <Calendar className="h-4 w-4 mr-2" />
              Tüm Maçlar
            </TabsTrigger>
            <TabsTrigger value="bookings">
              <Clock className="h-4 w-4 mr-2" />
              Tüm Rezervasyonlar
            </TabsTrigger>
            <TabsTrigger value="venues">
              <Building2 className="h-4 w-4 mr-2" />
              Tüm Sahalar
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <Label htmlFor="user-search">Ara</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="user-search"
                        placeholder="İsim veya email..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="w-full md:w-48">
                    <Label>Rol</Label>
                    <Select value={userRole} onValueChange={setUserRole}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tümü</SelectItem>
                        <SelectItem value="player">Oyuncu</SelectItem>
                        <SelectItem value="venue_owner">Saha Sahibi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Kullanıcı Listesi ({users.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Kullanıcı</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Rol</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Kayıt</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">İşlem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id} className={`border-b hover:bg-gray-50 ${u.isBlocked ? 'bg-red-50' : ''}`}>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <Avatar className="h-10 w-10">
                                  <AvatarImage src={u.profileImage} />
                                  <AvatarFallback className="bg-purple-100 text-purple-700">
                                    {u.name.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-semibold flex items-center gap-2">
                                    {u.name}
                                    {u.isBlocked && <Badge variant="destructive" className="text-xs">Engelli</Badge>}
                                  </p>
                                  {u.position && <p className="text-xs text-gray-500">{u.position}</p>}
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm">{u.email}</td>
                            <td className="py-3 px-4">
                              <Badge className={`${getRoleBadgeColor(u.role)} text-white`}>
                                {getRoleLabel(u.role)}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-sm">{formatDate(u.createdAt)}</td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => navigate(`/profile/${u.id}`)}
                                  title="Görüntüle"
                                >
                                  <Eye className="h-4 w-4 text-blue-600" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleBlockUser(u.id, u.isBlocked)}
                                  title={u.isBlocked ? "Engeli Kaldır" : "Engelle"}
                                  disabled={u.isAdmin}
                                >
                                  {u.isBlocked ? (
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <Ban className="h-4 w-4 text-orange-600" />
                                  )}
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleDeleteUser(u.id, u.name)}
                                  title="Sil"
                                  disabled={u.isAdmin}
                                  className="hover:bg-red-100"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Matches Tab */}
          <TabsContent value="matches">
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="w-full md:w-48">
                  <Label>Durum</Label>
                  <Select value={matchStatus} onValueChange={setMatchStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      <SelectItem value="open">Açık</SelectItem>
                      <SelectItem value="closed">Kapalı</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Maç Listesi ({matches.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {matches.map((match) => (
                      <Card key={match.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg">{match.venueName}</h3>
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  {match.date}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="h-4 w-4" />
                                  {match.time}
                                </span>
                                <span>Format: {match.format}</span>
                              </div>
                              <p className="text-sm text-gray-500 mt-1">
                                Oyuncu: {match.players?.length || 0}/{match.teamSize}
                              </p>
                            </div>
                            <Badge className={`${getStatusBadge(match.status).bg} text-white`}>
                              {getStatusBadge(match.status).text}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    {matches.length === 0 && (
                      <p className="text-center py-8 text-gray-500">Maç bulunamadı</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="w-full md:w-48">
                  <Label>Durum</Label>
                  <Select value={bookingStatus} onValueChange={setBookingStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      <SelectItem value="confirmed">Onaylı</SelectItem>
                      <SelectItem value="cancelled">İptal</SelectItem>
                      <SelectItem value="pending">Beklemede</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Rezervasyon Listesi ({bookings.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Kullanıcı</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Saha</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Tarih</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Saat</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Ücret</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings.map((booking) => (
                          <tr key={booking.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 text-sm">{booking.userName}</td>
                            <td className="py-3 px-4 text-sm">{booking.venueName}</td>
                            <td className="py-3 px-4 text-sm">{booking.date}</td>
                            <td className="py-3 px-4 text-sm">{booking.startTime}</td>
                            <td className="py-3 px-4 text-sm font-semibold">₺{parseFloat(booking.finalPrice).toFixed(2)}</td>
                            <td className="py-3 px-4">
                              <Badge className={`${getStatusBadge(booking.status).bg} text-white text-xs`}>
                                {getStatusBadge(booking.status).text}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {bookings.length === 0 && (
                      <p className="text-center py-8 text-gray-500">Rezervasyon bulunamadı</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Venues Tab */}
          <TabsContent value="venues">
            <Card>
              <CardHeader>
                <CardTitle>Saha Listesi ({venues.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {venues.map((venue) => (
                      <Card key={venue.id} className={`hover:shadow-lg transition-shadow ${venue.isBlocked ? 'border-red-300 bg-red-50' : ''}`}>
                        <CardContent className="pt-4">
                          {venue.images && venue.images.length > 0 && (
                            <img 
                              src={venue.images[0]} 
                              alt={venue.name}
                              className="w-full h-40 object-cover rounded-lg mb-3"
                            />
                          )}
                          <h3 className="font-semibold text-lg flex items-center gap-2">
                            {venue.name}
                            {venue.isBlocked && <Badge variant="destructive" className="text-xs">Engelli</Badge>}
                          </h3>
                          <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                            <MapPin className="h-4 w-4" />
                            {venue.location}
                          </div>
                          <div className="mt-3 flex justify-between items-center">
                            <span className="text-sm text-gray-600">{venue.format}</span>
                            <span className="font-semibold text-emerald-600">₺{venue.pricePerHour}/saat</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">Kapasite: {venue.capacity} kişi</p>
                          
                          {/* Action Buttons */}
                          <div className="mt-4 flex justify-end gap-2 border-t pt-3">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/venue/${venue.id}`)}
                              title="Görüntüle"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Görüntüle
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleBlockVenue(venue.id, venue.isBlocked)}
                              title={venue.isBlocked ? "Engeli Kaldır" : "Engelle"}
                              className={venue.isBlocked ? "text-green-600 border-green-600" : "text-orange-600 border-orange-600"}
                            >
                              {venue.isBlocked ? (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Aç
                                </>
                              ) : (
                                <>
                                  <Ban className="h-4 w-4 mr-1" />
                                  Engelle
                                </>
                              )}
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDeleteVenue(venue.id, venue.name)}
                              title="Sil"
                              className="text-red-600 border-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Sil
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    {venues.length === 0 && (
                      <p className="text-center py-8 text-gray-500 col-span-3">Saha bulunamadı</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: null, id: null, name: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">
              {deleteDialog.type === 'user' ? '🗑️ Kullanıcıyı Sil' : '🗑️ Sahayı Sil'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-gray-900">"{deleteDialog.name}"</span> {deleteDialog.type === 'user' ? 'kullanıcısını' : 'sahasını'} silmek istediğinize emin misiniz?
              <br /><br />
              <span className="text-red-500 font-medium">⚠️ Bu işlem geri alınamaz!</span>
              <br />
              {deleteDialog.type === 'user' 
                ? 'Kullanıcının tüm rezervasyonları, maçları ve başvuruları da silinecektir.'
                : 'Sahanın tüm rezervasyonları ve maçları da silinecektir.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Evet, Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPanel;
